import { useEffect, useState } from "react";
import { Loader2, Save, Package as PackageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/i18n";
import { fetchAdminPackages, updatePackage } from "@/lib/packages";
import { toast } from "sonner";

const LANGS = ["fi", "en", "sv"];

// Founder-only editor for the existing plans (service/assistant/strategist).
// Plan IDs, permissions and Stripe mapping are unchanged — this edits the
// editable display/business fields (prices, trial, credits, copy) that the
// single-source-of-truth config drives on the public pages and checkout.
export default function PackageEditor() {
  const { t } = useTranslation();
  const [pkgs, setPkgs] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [lang, setLang] = useState("fi");

  useEffect(() => {
    fetchAdminPackages().then(setPkgs).catch((e) => toast.error(e.response?.data?.detail || e.message));
  }, []);

  const setField = (idx, path, value) => {
    setPkgs((prev) => {
      const next = prev.map((p) => ({ ...p }));
      const p = next[idx];
      const keys = path.split(".");
      let obj = p;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...(obj[keys[i]] || {}) };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async (p) => {
    setSavingId(p.planId);
    try {
      const payload = {
        order: Number(p.order) || 0,
        visible: !!p.visible,
        recommended: !!p.recommended,
        name: p.name || {}, description: p.description || {}, badge: p.badge || {}, ctaText: p.ctaText || {},
        ctaDestination: p.ctaDestination || "",
        pricing: { monthly: Number(p.pricing?.monthly) || 0, yearly: Number(p.pricing?.yearly) || 0, currency: p.pricing?.currency || "EUR" },
        trial: { available: !!p.trial?.available, days: Number(p.trial?.days) || 0, price: Number(p.trial?.price) || 0 },
        durationDays: { monthly: Number(p.durationDays?.monthly) || 30, yearly: Number(p.durationDays?.yearly) || 365 },
        renewalModel: p.renewalModel || "recurring",
        credits: {
          standard: Number(p.credits?.standard) || 0, advanced: Number(p.credits?.advanced) || 0,
          total: Number(p.credits?.total) || 0, shared: !!p.credits?.shared, unlimited: !!p.credits?.unlimited,
          resetDays: p.credits?.resetDays == null || p.credits?.resetDays === "" ? null : Number(p.credits.resetDays),
        },
        features: Array.isArray(p.features) ? p.features : [],
      };
      const saved = await updatePackage(p.planId, payload);
      setPkgs((prev) => prev.map((x) => (x.planId === p.planId ? { ...saved } : x)));
      toast.success(t("admin.packages.saved", { name: p.planId }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSavingId(null);
    }
  };

  if (!pkgs) {
    return <div className="py-16 flex justify-center" data-testid="packages-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <section className="space-y-4" data-testid="admin-packages">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">{t("admin.packages.intro")}</p>
        <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="packages-lang-toggle">
          {LANGS.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`packages-lang-${l}`}
              className={["h-7 px-3 rounded-full text-xs font-medium uppercase transition", lang === l ? "bg-white shadow text-slate-900" : "text-slate-500"].join(" ")}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {pkgs.map((p, idx) => (
        <div key={p.planId} className="surface-card p-4 space-y-3" data-testid={`package-card-${p.planId}`}>
          <div className="flex items-center gap-2">
            <PackageIcon className="h-4 w-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900 capitalize">{p.planId}</h3>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-auto">ID: {p.planId} (locked)</span>
          </div>

          {/* Localized copy */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("admin.packages.name")}>
              <Input value={p.name?.[lang] || ""} onChange={(e) => setField(idx, `name.${lang}`, e.target.value)} data-testid={`pkg-${p.planId}-name`} />
            </Field>
            <Field label={t("admin.packages.badge")}>
              <Input value={p.badge?.[lang] || ""} onChange={(e) => setField(idx, `badge.${lang}`, e.target.value)} data-testid={`pkg-${p.planId}-badge`} />
            </Field>
            <Field label={t("admin.packages.description")} full>
              <Input value={p.description?.[lang] || ""} onChange={(e) => setField(idx, `description.${lang}`, e.target.value)} data-testid={`pkg-${p.planId}-desc`} />
            </Field>
            <Field label={t("admin.packages.ctaText")}>
              <Input value={p.ctaText?.[lang] || ""} onChange={(e) => setField(idx, `ctaText.${lang}`, e.target.value)} data-testid={`pkg-${p.planId}-cta`} />
            </Field>
            <Field label={t("admin.packages.ctaDest")}>
              <Input value={p.ctaDestination || ""} onChange={(e) => setField(idx, "ctaDestination", e.target.value)} data-testid={`pkg-${p.planId}-ctadest`} />
            </Field>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label={t("admin.packages.monthly")}>
              <Input type="number" step="0.01" value={p.pricing?.monthly ?? 0} onChange={(e) => setField(idx, "pricing.monthly", e.target.value)} data-testid={`pkg-${p.planId}-monthly`} />
            </Field>
            <Field label={t("admin.packages.yearly")}>
              <Input type="number" step="0.01" value={p.pricing?.yearly ?? 0} onChange={(e) => setField(idx, "pricing.yearly", e.target.value)} data-testid={`pkg-${p.planId}-yearly`} />
            </Field>
            <Field label={t("admin.packages.renewal")}>
              <select value={p.renewalModel || "recurring"} onChange={(e) => setField(idx, "renewalModel", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`pkg-${p.planId}-renewal`}>
                <option value="recurring">recurring</option>
                <option value="one_time">one_time</option>
                <option value="none">none</option>
              </select>
            </Field>
            <Field label={t("admin.packages.order")}>
              <Input type="number" value={p.order ?? 0} onChange={(e) => setField(idx, "order", e.target.value)} data-testid={`pkg-${p.planId}-order`} />
            </Field>
          </div>

          {/* Trial */}
          <div className="grid grid-cols-3 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.trial?.available} onChange={(e) => setField(idx, "trial.available", e.target.checked)} data-testid={`pkg-${p.planId}-trial-available`} />
              {t("admin.packages.trialAvailable")}
            </label>
            <Field label={t("admin.packages.trialDays")}>
              <Input type="number" value={p.trial?.days ?? 0} onChange={(e) => setField(idx, "trial.days", e.target.value)} data-testid={`pkg-${p.planId}-trial-days`} />
            </Field>
            <Field label={t("admin.packages.trialPrice")}>
              <Input type="number" step="0.01" value={p.trial?.price ?? 0} onChange={(e) => setField(idx, "trial.price", e.target.value)} data-testid={`pkg-${p.planId}-trial-price`} />
            </Field>
          </div>

          {/* Credits (business logic — drives permissions) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <Field label={t("admin.packages.standard")}>
              <Input type="number" value={p.credits?.standard ?? 0} onChange={(e) => setField(idx, "credits.standard", e.target.value)} data-testid={`pkg-${p.planId}-standard`} />
            </Field>
            <Field label={t("admin.packages.advanced")}>
              <Input type="number" value={p.credits?.advanced ?? 0} onChange={(e) => setField(idx, "credits.advanced", e.target.value)} data-testid={`pkg-${p.planId}-advanced`} />
            </Field>
            <Field label={t("admin.packages.total")}>
              <Input type="number" value={p.credits?.total ?? 0} onChange={(e) => setField(idx, "credits.total", e.target.value)} data-testid={`pkg-${p.planId}-total`} />
            </Field>
            <Field label={t("admin.packages.resetDays")}>
              <Input type="number" value={p.credits?.resetDays ?? ""} onChange={(e) => setField(idx, "credits.resetDays", e.target.value)} data-testid={`pkg-${p.planId}-resetdays`} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.credits?.shared} onChange={(e) => setField(idx, "credits.shared", e.target.checked)} data-testid={`pkg-${p.planId}-shared`} />
              {t("admin.packages.shared")}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.credits?.unlimited} onChange={(e) => setField(idx, "credits.unlimited", e.target.checked)} data-testid={`pkg-${p.planId}-unlimited`} />
              {t("admin.packages.unlimited")}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.visible} onChange={(e) => setField(idx, "visible", e.target.checked)} data-testid={`pkg-${p.planId}-visible`} />
              {t("admin.packages.visible")}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.recommended} onChange={(e) => setField(idx, "recommended", e.target.checked)} data-testid={`pkg-${p.planId}-recommended`} />
              {t("admin.packages.recommended")}
            </label>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save(p)} disabled={savingId === p.planId} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid={`pkg-${p.planId}-save`}>
              {savingId === p.planId ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {t("admin.packages.save")}
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
